/**
 * @file The composite sub-fields a row projection fills, grouped by the column
 * they land on, each carrying the sentence that says why it is locked. Consumer:
 * `ArRow`, which resolves it once per projected row and hands each cell its own
 * entry, exactly as it hands down `formulaSubFieldsByColumn`.
 * A dotted projection column writes `cells[col].value.fields[sub].value` and no
 * `_calculated` marker, because the derive owns ONE sub-field while the ones
 * beside it stay the person's — so this map, not the marker, is what locks them
 * and what explains the lock inside the composite sidebar.
 * The sentence is `deriveCellTooltipOfColumn`'s, the one the projected cell shows
 * on hover: a second spelling would let the cell and the sidebar word one derive
 * two ways. It cannot be `null` for a column taken from this projection's own
 * list, so the guard below is the type's rather than a case.
 * An undotted column contributes nothing: it claims the whole cell with
 * `_calculated`, which locks it on its own.
 */

import type { TypeId } from '#types';
import { deriveCellTooltipOfColumn } from './deriveCellTooltipOfColumn';
import type { ArTableDeriveProjection } from './resolveArTableDeriveProjections';

const NO_DERIVE_SUB_FIELDS: ReadonlyMap<TypeId, ReadonlyMap<string, string>> = new Map();

function deriveSubFieldsByColumn(
  projection: ArTableDeriveProjection | null,
): ReadonlyMap<TypeId, ReadonlyMap<string, string>> {
  if (projection === null) return NO_DERIVE_SUB_FIELDS;

  const byColumn = new Map<TypeId, Map<string, string>>();
  for (const { typeId, subFieldId } of projection.columns) {
    if (subFieldId === null) continue;
    const reason = deriveCellTooltipOfColumn(projection, typeId);
    if (reason === null) continue;
    const existing = byColumn.get(typeId);
    if (existing) {
      existing.set(subFieldId, reason);
    } else {
      byColumn.set(typeId, new Map([[subFieldId, reason]]));
    }
  }
  return byColumn;
}

export { deriveSubFieldsByColumn };
